import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import UserInfoModal from "../list-users/components/user-info-modal";
import { useUser } from "../../../contexts/user-context";
import { PlatformUser } from "../list-users/list-users";
import { useEthosNotification } from "../../../contexts/ethos-notification-context";
import { renderPaginator } from "../../../utils/functions";
import { useUserFee } from "../../../contexts/user-fee-context";
import ExportSingleUserModal from "../list-users/components/export-single-user-data-modal";

export interface IUserServiceFeeTranche {
  paid: boolean;
  amount: number;
  percentage: number;
  fee: number;
  paymentDate: Date;
  settlementDate?: Date | null;
}

export interface IUserServiceFee {
  _id: string;
  arrayTranche: IUserServiceFeeTranche[];
  fee: number;
  paidFee: number;
  amount: number;
  status: string;
  serviceCustomerData: {
    _id: string;
    customerData: {
      _id: string;
      first_name: string;
      last_name: string;
    };
    serviceData: {
      _id: string;
      displayName: string;
    };
  };
  serviceCustomerId: string;
  userId: string;
  updatedBy?: string | null;
  createdBy?: string | null;
}

const UserInfoPage = (props) => {
  const paramId = props.match?.params?.id;
  const id = paramId || props.fallbackUserId;
  const isAdmin = props.isAdmin || false;

  const LIMIT = 12;
  const [offset, setOffset] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [userAttachedService, setUserAttachedService] = useState<
    IUserServiceFee[] | null
  >(null);
  const [selectedServiceFee, setSelectedServiceFee] =
    useState<IUserServiceFee | null>(null);

  const [paginationElement, setPaginationElement] =
    useState<JSX.Element | null>(null);
  const [load, setLoad] = useState(true);
  const { getSingleUser } = useUser();
  const { getFeeUsersById } = useUserFee();
  const { handleError } = useEthosNotification();
  const [showExportModal, setShowExportModal] = useState(false);

  const getUserAttachedServiceData = useCallback(
    async (id: string) => {
      const { error, data } = await getFeeUsersById(id, LIMIT, offset);

      if (error) {
        handleError(error);
        setLoad(false);
        return;
      }

      const attachedServices = data?.data || [];
      const totalAttachedServices = data?.length ?? attachedServices.length;

      setUserAttachedService(attachedServices);
      renderPaginator(
        totalAttachedServices,
        LIMIT,
        setOffset,
        setPaginationElement,
        offset,
      );
      setLoad(false);
    },
    [getFeeUsersById, handleError, offset],
  );

  const getUser = useCallback(
    async (id: string) => {
      const { error, data } = await getSingleUser(id);

      if (error) {
        handleError(error);
        setLoad(false);
        return;
      }

      setUser(data);
    },
    [getSingleUser, handleError],
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    setLoad(true);
    setOffset(0);
    setUser(null);
    setUserAttachedService(null);
    getUser(id);
  }, [getUser, id]);

  useEffect(() => {
    if (!user?._id) {
      return;
    }

    getUserAttachedServiceData(user._id);
  }, [getUserAttachedServiceData, user]);

  return (
    <>
      {!load && user && userAttachedService ? (
        <>
          <div className="list-users-page">
            <div className="row my-3">
              <div className="col-md-4 mb-3">
                <div className="text-muted small">Nome</div>
                <div className="font-weight-bold">{user.first_name}</div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="text-muted small">Cognome</div>
                <div className="font-weight-bold">{user.last_name}</div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="text-muted small">Username</div>
                <div className="font-weight-bold">{user.username}</div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="text-muted small">Email</div>
                <div className="font-weight-bold text-truncate">
                  {user.email}
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <div className="text-muted small">Ruolo</div>
                <span>{user.role}</span>
              </div>
            </div>

            <div className="col-12 mt-1">
              {userAttachedService && userAttachedService.length > 0 ? (
                <nav className="pagination-section">
                  <ul className="pagination justify-content-center">
                    {paginationElement}
                  </ul>
                </nav>
              ) : null}
            </div>
            <div className="col-12 mb-3 text-end">
              <button
                className="btn btn-primary"
                onClick={() => setShowExportModal(true)}
              >
                Esporta
              </button>
            </div>
            <table className="table table-striped">
              <thead className="thead-dark">
                <tr>
                  <th>Tipologia</th>
                  <th>Cliente</th>
                  <th>Costo del servizio</th>
                  <th>Commisione liquidata</th>
                  <th>Commisione dovuta</th>
                  <th>Stato</th>
                  <th style={{ width: "5%" }}></th>
                </tr>
              </thead>
              <tbody>
                {userAttachedService && userAttachedService.length > 0 ? (
                  <>
                    {userAttachedService.map((user_service, i) => {
                      const {
                        serviceCustomerData: {
                          customerData: { _id, first_name, last_name },
                        },
                      } = user_service;
                      const userFullname = `${first_name} ${last_name}`;

                      return (
                        <tr key={i}>
                          <td>
                            <span className="fw-bold ">
                              <a
                                href={`/customers/${_id}/services/${user_service.serviceCustomerId}`}
                              >
                                {
                                  user_service.serviceCustomerData.serviceData
                                    .displayName
                                }
                              </a>
                            </span>
                          </td>

                          <td>
                            <span className="fw-bold">
                              <a href={"/customers/" + _id}>{userFullname}</a>
                            </span>
                          </td>
                          <td> {user_service.amount.toFixed(2)}€</td>
                          <td> {user_service.paidFee.toFixed(2)}€</td>
                          <td>{user_service.fee.toFixed(2)}€</td>
                          <td>
                            <span
                              className="fw-bold"
                              style={{
                                color:
                                  user_service.status === "TOPAY"
                                    ? "red"
                                    : "green",
                              }}
                            >
                              {user_service.status === "TOPAY"
                                ? "DA PAGARE"
                                : "PAGATA"}
                            </span>
                          </td>
                          <td>
                            <FontAwesomeIcon
                              style={{ cursor: "pointer" }}
                              icon={faEye}
                              onClick={() => {
                                setShowModal(!showModal);
                                setSelectedServiceFee(user_service);
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <th colSpan={7} style={{ textAlign: "center" }}>
                      Non è stato trovato nessun servizio collegato a questo
                      utente
                    </th>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {showExportModal && (
            <ExportSingleUserModal
              userId={user._id}
              isAdmin={isAdmin}
              closeModal={(v) => setShowExportModal(v)}
              onDone={() => {}}
            />
          )}
          {showModal && (
            <UserInfoModal
              serviceFee={selectedServiceFee}
              closeModal={(value) => {
                setShowModal(value);
                if (!value) setSelectedServiceFee(null);
              }}
              onSaved={(us_id) => {
                getUserAttachedServiceData(us_id);
              }}
              isAdmin={isAdmin}
            />
          )}
        </>
      ) : (
        <h2 className="text-center mt-3">Caricamento in corso...</h2>
      )}
    </>
  );
};

export default UserInfoPage;
